import { NODE_ACTIONS, SAMPLE_OUTCOMES, additionalFailures, describeFailure, attemptFailureCode, attemptFailureMessage, describeRetry, diagnosticFacts, scoredNotice } from '@/lib/failures'
import { Fragment, useState, type SyntheticEvent } from 'react'
import { api } from '@/api/client'
import { AGENT_TERMINATED, failedAttempts, type Attempt, type OutputFile } from '@/lib/runs'

/** The canonical failure class as one chip, with the full wording on hover. */
export function FailureChip({ errorClass, compact = false }: { errorClass?: string | null; compact?: boolean }) {
  const failure = describeFailure(errorClass)
  if (!failure) return null
  return <span className={`chip ${failure.definitionDefect ? 'err' : 'warn'} failure-chip`} title={`${failure.raw} — ${failure.meaning}`}>
    {!compact && <span className="failure-layer">{failure.layer}</span>}{failure.label}
  </span>
}

/** Everything one failed attempt says: what broke, what it meant for the
 * sample, what the queue did next, and what it did to the node.
 *
 * The header carries only what the body cannot: which attempt this was, and the two
 * verdicts that are about the queue and the node rather than about the error. The
 * class, its layer and the sample outcome all live in the facts below, and putting
 * them in the header too made a card that said `display_session_lost` three times. */
export const STOPPED_BY_AGENT = {
  label: 'Stopped by the authoring agent',
  meaning: 'The authoring agent ended this rollout on purpose after reading its telemetry. What the runner recorded is the shutdown it saw when the node was taken away, not the cause of anything, and the attempt counted as one non-infrastructure attempt on the authoring job.',
}

/** The attempt's stdout and stderr, read only when someone opens it: they can run to megabytes. */
function RunnerOutput({ runId, jobId, attemptId }: { runId: string; jobId: string; attemptId: string }) {
  const [files, setFiles] = useState<OutputFile[] | null>(null)
  const [error, setError] = useState('')
  const load = (event: SyntheticEvent<HTMLDetailsElement>) => {
    if (!event.currentTarget.open || files) return
    setError('')
    api.get<{ files: OutputFile[] }>(`/eng/runs/${runId}/rollouts/${jobId}/attempts/${attemptId}/output`)
      .then(value => setFiles(value.files), (reason: Error) => setError(reason.message))
  }
  return <details onToggle={load}>
    <summary>Show what the harness printed</summary>
    {error ? <p className="field-error" role="alert">{error}</p>
      : !files ? <p className="muted small">Reading…</p>
      : !files.length ? <p className="muted small">Nothing was recorded for this attempt.</p>
      : files.map(file => <div key={`${file.instance_id}/${file.path}`}>
          <strong>{file.name}</strong><span className="muted small"> · {file.instance_id || 'REMI'}
            {file.total_bytes > file.text.length && ` · last ${file.text.length.toLocaleString()} of ${file.total_bytes.toLocaleString()} bytes`}</span>
          <pre className="prewrap"><code>{file.text}</code></pre>
        </div>)}
  </details>
}

/** The diagnostic's named facts, as `<dt>`/`<dd>` pairs. Text nodes and allowlisted facts
 *  only: a failed attempt and a scored one with a diagnostic show exactly the same rows. */
function DiagnosticRows({ attempt }: { attempt: Attempt }) {
  const diagnostic = attempt.diagnostic
  return <>
    {diagnostic?.stage && <><dt>Stage</dt><dd>{diagnostic.stage}</dd></>}
    {diagnostic?.origin && <><dt>Reported by</dt><dd>{diagnostic.origin}</dd></>}
    {diagnostic?.exception_type && <><dt>Exception</dt><dd>{diagnostic.exception_type}</dd></>}
    {diagnostic?.diagnostic_id && <><dt>Diagnostic ID</dt><dd><code>{diagnostic.diagnostic_id}</code></dd></>}
    {diagnosticFacts(attempt).map(([label, value]) => <Fragment key={label}><dt>{label}</dt><dd>{value}</dd></Fragment>)}
    {diagnostic && attempt.error_class && attempt.error_class !== diagnostic.code && <><dt>Operational class</dt><dd><code>{attempt.error_class}</code></dd></>}
  </>
}

/** Failures recorded after the attempt's own (evidence upload, cleanup). The attempt's
 *  primary failure stays the cause and decides the retry; these are listed beside it and
 *  never tallied. Text nodes only. `standalone` gives them their own block, for an attempt
 *  that has no failure card to sit in (a scored one). */
export function AdditionalFailures({ attempt, standalone = false }: { attempt: Attempt; standalone?: boolean }) {
  const rows = additionalFailures(attempt)
  if (!rows.length) return null
  return <section className={`additional-failures${standalone ? ' standalone' : ''}`} aria-label="Additional failures">
    <div className="attempt-failure-head">
      <strong>Additional failures</strong>
      <span className="muted small">Same attempt · {standalone ? 'the score stands' : 'the class above stays the cause'} · not counted separately</span>
    </div>
    <ul>{rows.map((row, index) => <li key={`${row.code}-${row.diagnostic_id ?? index}`}>
      <div className="additional-failure-head"><code>{row.code}</code>{row.stage && <span className="muted small">stage {row.stage}</span>}</div>
      {row.message && <p>{row.message}</p>}
      {(row.diagnostic_id || row.exception_type) && <dl className="attempt-failure-facts">
        {row.exception_type && <><dt>Exception</dt><dd>{row.exception_type}</dd></>}
        {row.diagnostic_id && <><dt>Diagnostic ID</dt><dd><code>{row.diagnostic_id}</code></dd></>}
      </dl>}
    </li>)}</ul>
  </section>
}

/** "+N additional" beside the compact row's message: the extra failures belong to the same
 *  attempt, so they are a marker, never a count of failures. */
export function AdditionalMarker({ attempt }: { attempt?: Attempt }) {
  const count = additionalFailures(attempt).length
  return count ? <span className="chip warn additional-marker">+{count} additional</span> : null
}

/** A scored attempt's ending as one chip: orange, because the sample needs a look, never
 *  red, because nothing failed. */
export function ScoredChip({ attempt }: { attempt?: Attempt }) {
  const notice = scoredNotice(attempt)
  return notice ? <span className="chip warn scored-chip">{notice.label}</span> : null
}

/** Why a scored attempt ended, when the runtime recorded more than its score (a model
 *  refusal is a scored 0 with an OpenRouter diagnostic). It is not a failure card: the
 *  attempt counts as a sample and stays out of every failure tally. */
export function ScoredDiagnostic({ attempt }: { attempt: Attempt }) {
  const notice = scoredNotice(attempt)
  if (!notice) return null
  const code = describeFailure(notice.code)
  return <div className="attempt-notice">
    <div className="attempt-failure-head">
      <ScoredChip attempt={attempt} />
      <span className="muted small">Scored sample · not counted as a failure</span>
    </div>
    <p className="attempt-failure-meaning">{notice.message || notice.meaning}</p>
    <dl className="attempt-failure-facts">
      {code && <><dt>Diagnostic</dt><dd><code>{code.raw}</code><span className="muted small"> · {code.layer}{!code.known && ', unknown to this page'}</span></dd></>}
      <DiagnosticRows attempt={attempt} />
    </dl>
  </div>
}

export function AttemptFailure({ attempt, ordinal, output }: {
  attempt: Attempt; ordinal: number; output?: { runId: string; jobId: string }
}) {
  const failure = describeFailure(attemptFailureCode(attempt))
  const message = attemptFailureMessage(attempt)
  const outcome = SAMPLE_OUTCOMES[attempt.sample_outcome ?? '']
  const retry = describeRetry(attempt)
  const action = NODE_ACTIONS[attempt.node_action ?? '']
  // The platform outranks the runner here: only it knows the agent chose to stop.
  const stopped = attempt.stopped_by_agent === true || attempt.classification === AGENT_TERMINATED
  const logs = Object.entries(attempt.runner_logs ?? {}).sort(([a], [b]) => a.localeCompare(b))
  return <div className={`attempt-failure ${failure?.definitionDefect && !stopped ? 'defect' : ''}`}>
    <div className="attempt-failure-head">
      <strong>Attempt {ordinal}</strong>
      {stopped && <span className="chip neutral" title={STOPPED_BY_AGENT.meaning}>{STOPPED_BY_AGENT.label}</span>}
      {!stopped && !failure && <span className="chip warn">Failed</span>}
      {!stopped && retry && <span className="chip neutral" title={retry.meaning}>{retry.label}</span>}
      {action && <span className="chip err" title={action.meaning}>{action.label}</span>}
    </div>
    {stopped ? <p className="attempt-failure-meaning">{STOPPED_BY_AGENT.meaning}</p>
      : (message || failure) && <p className="attempt-failure-meaning">{message || failure?.meaning}</p>}
    <dl className="attempt-failure-facts">
      {failure && <><dt>{stopped ? 'Runner recorded' : 'Class'}</dt><dd><code>{failure.raw}</code><span className="muted small"> · {failure.layer}{stopped && ' · the shutdown, not the cause'}{failure.legacy && ', recorded by an older runner release'}{!failure.known && ', unknown to this page'}</span></dd></>}
      {!stopped && outcome && <><dt>Sample</dt><dd>{outcome.label}<span className="muted small"> · {outcome.meaning}</span></dd></>}
      {!stopped && retry && <><dt>Recovery</dt><dd>{retry.label} · {retry.meaning}</dd></>}
      {attempt.model_started != null && <><dt>Model execution started</dt><dd>{attempt.model_started ? 'Yes' : 'No'}</dd></>}
      <DiagnosticRows attempt={attempt} />

      {!logs.length && attempt.has_output && output && attempt.attempt_id && <><dt>Runner output</dt>
        <dd><RunnerOutput runId={output.runId} jobId={output.jobId} attemptId={attempt.attempt_id} /></dd></>}
      {logs.length > 0 && <><dt>Runner output</dt><dd>{logs.map(([name, url], index) => <span key={name}>{index > 0 && ' · '}<a href={url} target="_blank" rel="noreferrer">{name}</a></span>)}<span className="muted small"> · what the harness printed on the node, shipped every heartbeat</span></dd></>}
      {attempt.detail && attempt.detail !== message && <><dt>Detail</dt><dd className="prewrap"><code>{attempt.detail}</code></dd></>}
    </dl>
    <AdditionalFailures attempt={attempt} />
  </div>
}

/** Run-level tally of what went wrong, grouped by the layer that failed. */
export function FailureBreakdown({ attempts }: { attempts: Attempt[] }) {
  // An agent-stopped rollout is not a failure of any layer, so it is not tallied here;
  // the attempt card says what it was.
  const failed = failedAttempts(attempts).filter(attempt => attempt.stopped_by_agent !== true)
  if (!failed.length) return null
  const counts = new Map<string, { label: string; layer: string; meaning: string; count: number }>()
  for (const attempt of failed) {
    const failure = describeFailure(attemptFailureCode(attempt)) ?? describeFailure('runtime.unknown')!
    const row = counts.get(failure.raw)
    if (row) row.count += 1
    else counts.set(failure.raw, { label: failure.label, layer: failure.layer, meaning: attemptFailureMessage(attempt) || failure.meaning, count: 1 })
  }
  const rows = [...counts.entries()].sort((a, b) => b[1].count - a[1].count)
  return <section className="section failure-breakdown">
    <div className="section-head"><h2>Failures</h2><span className="muted small">{failed.length} failed {failed.length === 1 ? 'attempt' : 'attempts'}</span></div>
    <ul className="failure-list">{rows.map(([key, row]) => <li key={key}>
      <span className="failure-count">{row.count}×</span>
      <span><strong>{row.label}</strong> <span className="failure-layer">{row.layer}</span><br /><span className="muted small">{row.meaning}</span></span>
      <code className="muted small">{key}</code>
    </li>)}</ul>
  </section>
}
